import { getConfig, getEnvironment } from '@/config'
import { getTransactionErrorMessage } from '@/utils/error'
import type { Role, RolesMatchType } from '@trinity/blockchain-management'
import { useManagementContractQuery } from '@trinity/blockchain-management-react'
import { HasRoleQuery } from '@trinity/blockchain-management-react'
import { useWallet } from '@trinity/wallet-react'
import type { FC } from 'react'
import { WalletGuard } from './wallet-guard'

const environment = getEnvironment()
const config = getConfig(environment, import.meta.env)
const { management: managementContractAddress } = config.contractAddresses
interface Props {
    children: React.ReactNode
    roles: Role[]
    match: RolesMatchType
    unauthorizedElement?: React.ReactNode
}

const RoleGuardContent: FC<Props> = ({ children, roles, match, unauthorizedElement = null }) => {
    const { address = '', isReady, isConnected } = useWallet()
    const {
        data: hasRole = false,
        isLoading,
        error,
    } = useManagementContractQuery(HasRoleQuery({ address, roles, match }))
    const errorMessage = getTransactionErrorMessage(error)

    if (isLoading || !isReady) {
        return null
    }
    if (!hasRole || !isConnected) {
        return unauthorizedElement
    }

    if (
        typeof errorMessage === 'string' &&
        (errorMessage?.startsWith('INVALID_ARGUMENT') || errorMessage?.startsWith('BAD_DATA'))
    ) {
        // Most likely the management contract is not deployed yet
        return <div>Management contract not deployed at address {managementContractAddress}</div>
    }

    return children
}

export const RoleGuard: FC<Props> = ({ children, roles, match, unauthorizedElement }) => {
    if (roles.length === 0) {
        // No roles required, so we can render the children immediately
        return children
    }

    return (
        <WalletGuard noWalletElement={unauthorizedElement}>
            <RoleGuardContent roles={roles} match={match} unauthorizedElement={unauthorizedElement}>
                {children}
            </RoleGuardContent>
        </WalletGuard>
    )
}
